// src/paginas/IniciarSesion.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google'; // Importar GoogleLogin
import { Link } from 'react-router-dom';
import { useAuth } from '../contexto/AuthContext'; // Importar useAuth
import { API_URL } from '../servicios/api';

const IniciarSesion = () => {
  const [correo, setCorreo] = useState('');
  const [contrasena, setContrasena] = useState('');
  const [mostrarContrasena, setMostrarContrasena] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [mensaje, setMensaje] = useState(''); // Para mostrar mensajes al usuario
  const [tipoMensaje, setTipoMensaje] = useState(''); // 'exito' o 'error'
  const navigate = useNavigate(); // Hook para la navegación programática
  const { login } = useAuth(); // Usar el hook useAuth para acceder a la función login

  const manejarEnvio = async (e) => {
    e.preventDefault();
    setMensaje(''); // Limpiar mensajes anteriores
    setEnviando(true);

    try {
      const response = await fetch(`${API_URL}/login.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          correo_electronico: correo,
          contrasena: contrasena,
        }),
      });

      const datos = await response.json();

      if (datos.exito) {
        setMensaje(datos.mensaje);
        setTipoMensaje('exito');
        login(datos.token, datos.usuario);
        navigate('/dashboard');
      } else {
        setMensaje(datos.mensaje);
        setTipoMensaje('error');
      }
    } catch (error) {
      console.error('Error al iniciar sesión:', error);
      setMensaje('Error de conexión con el servidor. Inténtalo de nuevo más tarde.');
      setTipoMensaje('error');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-dark-background px-4 py-10">
      {/* Resplandores decorativos de fondo */}
      <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-brand-orange/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-sky-500/10 blur-3xl" />

      <div className="relative w-full max-w-md">
        <div className="rounded-2xl border border-white/10 bg-gray-900/80 p-8 shadow-2xl shadow-black/40 backdrop-blur-sm">
          {/* Encabezado con icono */}
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-orange/15 ring-1 ring-brand-orange/30">
            <svg viewBox="0 0 24 24" fill="none" className="h-7 w-7 text-brand-orange" stroke="currentColor" strokeWidth="1.8">
              <rect x="4" y="10" width="16" height="10" rx="2" strokeLinecap="round" strokeLinejoin="round" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 10V7a4 4 0 0 1 8 0v3" />
              <circle cx="12" cy="15" r="1.5" />
            </svg>
          </div>
          <h3 className="mt-4 text-center text-2xl font-bold tracking-tight text-white">Iniciar sesión</h3>
          <p className="mt-1 text-center text-sm leading-relaxed text-gray-400">
            Accede con tu correo y contraseña o con tu cuenta de Google.
          </p>

          {mensaje && (
            <div
              role="alert"
              className={`mt-5 flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm ${
                tipoMensaje === 'exito'
                  ? 'border-emerald-800 bg-emerald-950/40 text-emerald-200'
                  : 'border-red-800 bg-red-950/40 text-red-200'
              }`}
            >
              <svg viewBox="0 0 24 24" fill="none" className="mt-0.5 h-4 w-4 flex-shrink-0" stroke="currentColor" strokeWidth="2">
                {tipoMensaje === 'exito' ? (
                  <path strokeLinecap="round" strokeLinejoin="round" d="m5 13 4 4L19 7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.3 3.86 1.8 18a1 1 0 0 0 .86 1.5h18.68a1 1 0 0 0 .86-1.5L13.7 3.86a1 1 0 0 0-1.7 0Z" />
                )}
              </svg>
              <span>{mensaje}</span>
            </div>
          )}

          <form onSubmit={manejarEnvio} className="mt-6 space-y-4">
            {/* Correo */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-400" htmlFor="correo">
                Correo electrónico
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                  <svg viewBox="0 0 24 24" fill="none" className="h-[18px] w-[18px]" stroke="currentColor" strokeWidth="1.8">
                    <rect x="3" y="5" width="18" height="14" rx="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="m4 7 8 6 8-6" />
                  </svg>
                </span>
                <input
                  type="email"
                  placeholder="tucorreo@ejemplo.com"
                  className="w-full rounded-lg border border-gray-700 bg-gray-800/80 py-2.5 pl-10 pr-3 text-sm text-white placeholder:text-gray-500 transition focus:border-brand-orange focus:outline-none focus:ring-1 focus:ring-brand-orange"
                  id="correo"
                  value={correo}
                  onChange={(e) => setCorreo(e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Contraseña */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-400" htmlFor="contrasena">
                Contraseña
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                  <svg viewBox="0 0 24 24" fill="none" className="h-[18px] w-[18px]" stroke="currentColor" strokeWidth="1.8">
                    <rect x="5" y="11" width="14" height="9" rx="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 11V8a4 4 0 0 1 8 0v3" />
                  </svg>
                </span>
                <input
                  type={mostrarContrasena ? 'text' : 'password'}
                  placeholder="Tu contraseña"
                  className="w-full rounded-lg border border-gray-700 bg-gray-800/80 py-2.5 pl-10 pr-10 text-sm text-white placeholder:text-gray-500 transition focus:border-brand-orange focus:outline-none focus:ring-1 focus:ring-brand-orange"
                  id="contrasena"
                  value={contrasena}
                  onChange={(e) => setContrasena(e.target.value)}
                  required
                />
                <button
                  type="button"
                  onClick={() => setMostrarContrasena((valor) => !valor)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 transition hover:text-brand-orange"
                  aria-label={mostrarContrasena ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {mostrarContrasena ? (
                    <svg viewBox="0 0 24 24" fill="none" className="h-[18px] w-[18px]" stroke="currentColor" strokeWidth="1.8">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18M10.6 10.6a2 2 0 0 0 2.8 2.8M9.4 5.5A10.4 10.4 0 0 1 12 5c5 0 9 4 10 7-.4 1.1-1.1 2.3-2.1 3.4M6.6 6.6C4.6 8 3.4 9.8 3 12c1 3 5 7 9 7 1.3 0 2.5-.3 3.6-.9" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" className="h-[18px] w-[18px]" stroke="currentColor" strokeWidth="1.8">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7Z" />
                      <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
              </div>
              <div className="mt-1.5 text-right">
                <Link to="/solicitar-recuperacion" className="text-xs text-gray-400 hover:text-brand-orange hover:underline">
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
            </div>

            <button
              type="submit"
              disabled={enviando}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand-orange py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-orange/20 transition hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {enviando && (
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.4 0 0 5.4 0 12h4Z" />
                </svg>
              )}
              {enviando ? 'Accediendo...' : 'Iniciar sesión'}
            </button>

            {/* Separador */}
            <div className="flex items-center gap-3 pt-1">
              <span className="h-px flex-1 bg-gray-700" />
              <span className="text-xs uppercase tracking-wide text-gray-500">o continúa con</span>
              <span className="h-px flex-1 bg-gray-700" />
            </div>

            <div className="flex justify-center [&>div]:w-full">
              <GoogleLogin
                onSuccess={async (credentialResponse) => {
                  console.log("Google credential received:", credentialResponse);
                  setMensaje(''); // Limpiar mensajes anteriores
                  try {
                    const response = await fetch(`${API_URL}/login_google.php`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({ credential: credentialResponse.credential }),
                    });

                    const datos = await response.json();

                    if (datos.exito) {
                      setMensaje(datos.mensaje);
                      setTipoMensaje('exito');
                      login(datos.token, datos.usuario);
                      navigate('/dashboard');
                    } else {
                      setMensaje(datos.mensaje);
                      setTipoMensaje('error');
                    }
                  } catch (error) {
                    console.error('Error al iniciar sesión con Google:', error);
                    setMensaje('Error de conexión con el servidor al intentar iniciar sesión con Google.');
                    setTipoMensaje('error');
                  }
                }}
                onError={() => {
                  console.log('Login Failed');
                  setMensaje('Error al iniciar sesión con Google. Inténtalo de nuevo.');
                  setTipoMensaje('error');
                }}
              />
            </div>

            <div className="pt-1 text-center text-sm text-gray-400">
              ¿Aún no tienes cuenta?{' '}
              <Link to="/solicitar-acceso" className="font-medium text-brand-orange hover:underline">
                Solicita acceso
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default IniciarSesion;
